import maplibregl from 'maplibre-gl';
import type { Scene, MapAdapter } from '../types';

const TITILER = 'https://titiler-centinela-746476722093.us-central1.run.app';
const CSV_URL = 'https://storage.googleapis.com/centinela-test-data/openclaw/sapi_agb_hydro_samples.csv';

const FLOW_FIELD_COG = 'gs://centinela-test-data/openclaw/flow/flow_field_rgba.tif';
const STREAM_COG     = 'gs://centinela-test-data/openclaw/flow/stream_order_cog.tif';
const S1_COG         = 'gs://centinela-test-data/openclaw/flow/s1_moisture_rgba.tif';

const MAX_SCATTER_PTS = 250; // subsample cap for scatter plot performance

type HydroLayerId = 'twi' | 'stream-order' | 's1-moisture';

interface HydroLayerDef {
  id:      HydroLayerId;
  srcId:   string;
  lyrId:   string;
  label:   string;
  desc:    string;
  tileUrl: string;
  opacity: number;
  legend:  { color: string; label: string }[];
}

const BASE = (cog: string) =>
  `${TITILER}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}?url=${encodeURIComponent(cog)}`;

const LAYERS: HydroLayerDef[] = [
  {
    id:      'twi',
    srcId:   'twi-src',
    lyrId:   'twi-layer',
    label:   'Wetness Index',
    desc:    'Topographic Wetness Index (TWI, 0–255). High values identify natural pooling zones ' +
             'and priority sites for topographic interventions — check dams, soil berms, and flow ' +
             'diversion — that can extend the effective rainy season and raise shallow groundwater.',
    tileUrl: `${BASE(FLOW_FIELD_COG)}&bidx=4&rescale=0,255&colormap_name=ylgnbu&resampling=bilinear&nodata=0`,
    opacity: 0.80,
    legend: [
      { color: '#ffffd9', label: 'Low TWI · runoff'       },
      { color: '#41b6c4', label: 'Moderate accumulation'   },
      { color: '#081d58', label: 'High TWI · pooling zone' },
    ],
  },
  {
    id:      'stream-order',
    srcId:   'stream-order-src',
    lyrId:   'stream-order-layer',
    label:   'Stream Network',
    desc:    'Strahler stream order from the 5 m COP-DEM. First-order headwaters are the primary ' +
             'target for retention structures; higher-order channels benefit from riparian ' +
             'buffer management to slow flood pulses and increase bank infiltration.',
    tileUrl: `${BASE(STREAM_COG)}&bidx=1&rescale=1,6&colormap_name=blues&nodata=0&resampling=nearest`,
    opacity: 0.90,
    legend: [
      { color: '#deebf7', label: 'Order 1 · headwater' },
      { color: '#6baed6', label: 'Order 2'             },
      { color: '#08306b', label: 'Order 3+ · main stem' },
    ],
  },
  {
    id:      's1-moisture',
    srcId:   's1-moisture-src',
    lyrId:   's1-moisture-layer',
    label:   'Soil Moisture',
    desc:    'Sentinel-1 SAR backscatter composite (dB). Low backscatter indicates dry, sandy ' +
             'soils; high values indicate wet soils or seasonal surface water — revealing moisture ' +
             'retention capacity and tracking the footprint of hydrological interventions over time.',
    tileUrl: `${BASE(S1_COG)}&bidx=3&rescale=-3,5&colormap_name=rdbu&resampling=bilinear`,
    opacity: 0.75,
    legend: [
      { color: '#1a1a2e', label: 'Dry · low backscatter' },
      { color: '#4a90d9', label: 'Moderate moisture'     },
      { color: '#e8f4fd', label: 'Wet · surface water'   },
    ],
  },
];

// ── Linear regression ────────────────────────────────────────────────────────
function linReg(xs: number[], ys: number[]): { slope: number; intercept: number; r2: number } {
  const n = xs.length;
  const mx = xs.reduce((a, b) => a + b, 0) / n;
  const my = ys.reduce((a, b) => a + b, 0) / n;
  let num = 0, den = 0, ssTot = 0, ssRes = 0;
  for (let i = 0; i < n; i++) {
    num += (xs[i] - mx) * (ys[i] - my);
    den += (xs[i] - mx) ** 2;
  }
  const slope = den === 0 ? 0 : num / den;
  const intercept = my - slope * mx;
  for (let i = 0; i < n; i++) {
    const pred = slope * xs[i] + intercept;
    ssRes += (ys[i] - pred) ** 2;
    ssTot += (ys[i] - my)  ** 2;
  }
  const r2 = ssTot === 0 ? 0 : 1 - ssRes / ssTot;
  return { slope, intercept, r2 };
}

// ── SVG scatter builder ───────────────────────────────────────────────────────
function makeScatter(opts: {
  xs: number[]; ys: number[];
  xLabel: string; yLabel: string;
  xMin?: number; xMax?: number;
  color?: string;
  regression?: boolean;
}): SVGSVGElement {
  const { xs, ys, xLabel, yLabel, color = '#41b6c4', regression = false } = opts;
  const W = 340, H = 180, PL = 40, PR = 14, PT = 10, PB = 28;
  const IW = W - PL - PR, IH = H - PT - PB;

  const xMin = opts.xMin ?? Math.min(...xs);
  const xMax = opts.xMax ?? Math.max(...xs);
  const yMin = Math.min(...ys);
  const yMax = Math.max(...ys);
  const xPad = (xMax - xMin) * 0.04;
  const yPad = (yMax - yMin) * 0.06;

  const xS = (v: number) => PL + ((v - xMin + xPad) / (xMax - xMin + 2 * xPad)) * IW;
  const yS = (v: number) => PT + IH - ((v - yMin + yPad) / (yMax - yMin + 2 * yPad)) * IH;

  const NS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(NS, 'svg') as SVGSVGElement;
  svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
  svg.setAttribute('width', String(W));
  svg.setAttribute('height', String(H));
  (svg as SVGElement & { style: CSSStyleDeclaration }).style.overflow = 'visible';

  const el = <K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string>) => {
    const e = document.createElementNS(NS, tag);
    Object.entries(attrs).forEach(([k, v]) => e.setAttribute(k, v));
    return e;
  };

  // Y grid + ticks
  [0, 0.25, 0.5, 0.75, 1].forEach(f => {
    const v = yMin + f * (yMax - yMin);
    const y = yS(v);
    svg.appendChild(el('line', { x1: String(PL), x2: String(W - PR), y1: String(y), y2: String(y), stroke: 'rgba(255,255,255,0.05)', 'stroke-width': '0.5' }));
    const t = el('text', { x: String(PL - 4), y: String(y), 'text-anchor': 'end', 'dominant-baseline': 'middle', 'font-size': '7', fill: '#6b8091' });
    t.textContent = v.toFixed(0);
    svg.appendChild(t);
  });

  // X ticks — 5 evenly spaced; use integer format for large-range axes (e.g. TWI 0–255)
  const xDecimals = (xMax - xMin) >= 10 ? 0 : 2;
  [0, 0.25, 0.5, 0.75, 1].forEach(f => {
    const v = xMin + f * (xMax - xMin);
    const x = xS(v);
    const t = el('text', { x: String(x), y: String(H - PB + 10), 'text-anchor': 'middle', 'font-size': '7', fill: '#6b8091' });
    t.textContent = v.toFixed(xDecimals);
    svg.appendChild(t);
  });

  // Axis labels
  const yLbl = el('text', { x: '8', y: String(PT + IH / 2), 'text-anchor': 'middle', 'font-size': '7', fill: '#6b8091', transform: `rotate(-90, 8, ${PT + IH / 2})` });
  yLbl.textContent = yLabel;
  svg.appendChild(yLbl);

  const xLbl = el('text', { x: String(PL + IW / 2), y: String(H - 2), 'text-anchor': 'middle', 'font-size': '7', fill: '#6b8091' });
  xLbl.textContent = xLabel;
  svg.appendChild(xLbl);

  // Dots
  for (let i = 0; i < xs.length; i++) {
    const dot = el('circle', { cx: String(xS(xs[i])), cy: String(yS(ys[i])), r: '2', fill: color, opacity: '0.40' });
    svg.appendChild(dot);
  }

  // Regression line
  if (regression && xs.length > 2) {
    const reg = linReg(xs, ys);
    const x0 = xMin, x1 = xMax;
    const y0 = reg.slope * x0 + reg.intercept;
    const y1 = reg.slope * x1 + reg.intercept;
    svg.appendChild(el('line', {
      x1: String(xS(x0)), y1: String(yS(y0)),
      x2: String(xS(x1)), y2: String(yS(y1)),
      stroke: '#D4B346', 'stroke-width': '1.8', 'stroke-dasharray': '4 2', opacity: '0.9',
    }));
    // R² label
    const r2Lbl = el('text', { x: String(W - PR - 2), y: String(PT + 10), 'text-anchor': 'end', 'font-size': '7.5', fill: '#D4B346', opacity: '0.85' });
    r2Lbl.textContent = `R² = ${reg.r2.toFixed(2)}`;
    svg.appendChild(r2Lbl);
  }

  return svg;
}

// ── Scene class ───────────────────────────────────────────────────────────────

export class SceneHydroManagement implements Scene {
  readonly panel: HTMLElement;
  private mapLegend: HTMLElement | null = null;
  private activeId: HydroLayerId = 'twi';
  private built = false;
  private chartBuilt = false;

  constructor(
    root: HTMLElement,
    private readonly mapAdapter: MapAdapter,
  ) {
    this.panel = document.createElement('div');
    this.panel.className = 'sm-panel';
    this.panel.dataset['scene'] = 'hydro-management';
    this.panel.innerHTML = `
      <div class="sm-panel-header">
        <div class="sm-panel-title">Hydrology Management</div>
        <div class="sm-panel-sub">TWI · Stream Network · Sentinel-2 Drydown · COP-DEM 5 m</div>
      </div>

      <div class="sm-headline">
        With every intervention to increase hydrology, carbon will follow — reducing
        soil moisture gradients or modifying terrain to increase topographic pooling
        directly expands the carbon-productive fraction of the landscape.
      </div>

      <div class="sm-hydro-layers">
        <div class="sm-hydro-layers-label">Map Layer</div>
        <div class="sm-hydro-layer-btns" id="sm-hydromgmt-layer-btns">
          ${LAYERS.map(l => `
            <button class="sm-hydro-layer-btn${l.id === 'twi' ? ' active' : ''}"
                    data-layer="${l.id}">${l.label}</button>`).join('')}
        </div>
        <div class="sm-hydro-layer-desc" id="sm-hydromgmt-layer-desc"></div>
      </div>

      <div class="sm-hydro-mgmt-divider"></div>

      <div class="sm-hydro-mgmt-insight">
        <div class="sm-hydro-mgmt-insight-title">Unlocking Biomass Through Hydrology</div>
        <div class="sm-hydro-mgmt-insight-body">
          AGB in this region is tightly bound to how the landscape captures and processes
          seasonal moisture. By implementing practices that allow the landscape to hold
          <strong>10% more soil moisture at the onset of the dry season</strong>, we create the
          carrying capacity to support an additional
          <span class="sm-hydro-stat-callout">+1.71 t biomass / ha</span>.
        </div>
        <div class="sm-hydro-mgmt-insight-body" style="margin-top:6px;">
          As the ecosystem recovers, trees act as a natural water pump. A 10% acceleration
          in the rate at which the landscape processes moisture during the dry season isn't
          a sign of drought — it is the exact signature of a thriving canopy drawing water
          from the soil.
        </div>
      </div>

      <div class="sm-hydro-mgmt-chart-title">
        S2 Surface Moisture at Dry-Season Onset (Apr 1) vs. Above-Ground Biomass
      </div>
      <div id="sm-hydromgmt-water-chart-wrap" class="sm-hydro-mgmt-chart-wrap">
        <div class="sm-hydro-loading" id="sm-hydromgmt-loading">
          <div class="sm-hydro-spinner"></div>
          <span class="sm-hydro-loading-text">Loading field data…</span>
        </div>
      </div>

      <div class="sm-hydro-mgmt-chart-title" style="margin-top:14px;">
        Topographic Wetness Index (TWI) vs. Above-Ground Biomass
      </div>
      <div id="sm-hydromgmt-twi-chart-wrap" class="sm-hydro-mgmt-chart-wrap"></div>

      <div class="sm-hydro-stats">
        <div class="sm-hydro-stat">
          <span>Moisture signal</span><strong>S2 Apr 1</strong>
        </div>
        <div class="sm-hydro-stat">
          <span>+AGB / 10% moisture</span><strong>1.71 t/ha</strong>
        </div>
        <div class="sm-hydro-stat">
          <span>DEM</span><strong>COP-DEM 5 m</strong>
        </div>
      </div>`;

    root.appendChild(this.panel);

    this.mapLegend = document.createElement('div');
    this.mapLegend.className = 'sm-map-layer-legend';
    this.mapLegend.style.display = 'none';
    document.getElementById('sm-map')?.appendChild(this.mapLegend);
  }

  onEnter(): void {
    if (!this.built) {
      const btns = this.panel.querySelector<HTMLElement>('#sm-hydromgmt-layer-btns')!;
      btns.addEventListener('click', (e) => {
        const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.sm-hydro-layer-btn');
        if (!btn) return;
        btns.querySelectorAll('.sm-hydro-layer-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.showLayer(btn.dataset['layer'] as HydroLayerId);
      });
      this.built = true;
    }

    if (!this.chartBuilt) {
      this.chartBuilt = true;
      this.buildCharts();
    }

    this.showLayer(this.activeId);
  }

  onLeave(): void {
    const map = this.rawMap();
    LAYERS.forEach(l => {
      if (map.getLayer(l.lyrId)) map.removeLayer(l.lyrId);
      if (map.getSource(l.srcId)) map.removeSource(l.srcId);
    });
    if (this.mapLegend) this.mapLegend.style.display = 'none';
  }

  private showLayer(id: HydroLayerId): void {
    this.activeId = id;
    const map = this.rawMap();

    LAYERS.forEach(l => {
      if (map.getLayer(l.lyrId)) map.removeLayer(l.lyrId);
      if (map.getSource(l.srcId)) map.removeSource(l.srcId);
    });

    const def = LAYERS.find(l => l.id === id)!;
    map.addSource(def.srcId, { type: 'raster', tiles: [def.tileUrl], tileSize: 256 });
    const firstSymbol = map.getStyle().layers?.find(l => l.type === 'symbol')?.id;
    map.addLayer(
      { id: def.lyrId, type: 'raster', source: def.srcId, paint: { 'raster-opacity': def.opacity } },
      firstSymbol,
    );

    const descEl = this.panel.querySelector<HTMLElement>('#sm-hydromgmt-layer-desc')!;
    descEl.textContent = def.desc;

    if (this.mapLegend) {
      this.mapLegend.innerHTML =
        `<div class="sm-map-legend-title">${def.label}</div>` +
        def.legend.map(s =>
          `<div class="sm-map-legend-row">
            <span class="sm-map-legend-swatch" style="background:${s.color};border:1px solid rgba(255,255,255,0.12)"></span>
            <span class="sm-map-legend-label">${s.label}</span>
          </div>`
        ).join('');
      this.mapLegend.style.display = 'block';
    }
  }

  private async buildCharts(): Promise<void> {
    const loadingEl = this.panel.querySelector<HTMLElement>('#sm-hydromgmt-loading')!;
    const waterWrap = this.panel.querySelector<HTMLElement>('#sm-hydromgmt-water-chart-wrap')!;
    const twiWrap   = this.panel.querySelector<HTMLElement>('#sm-hydromgmt-twi-chart-wrap')!;

    try {
      const res  = await fetch(CSV_URL);
      const text = await res.text();
      const lines = text.trim().split('\n');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());

      const col = (name: string) => headers.indexOf(name);
      const aprIdx = col('s2_apr1_intercept');
      const twiIdx = col('twi_scaled_0_255');
      const agbIdx = col('agb_mgc_ha');

      if (aprIdx < 0 || twiIdx < 0 || agbIdx < 0) {
        loadingEl.style.display = 'none';
        return;
      }

      interface Row { apr: number; twi: number; agb: number; }
      const allRows: Row[] = lines.slice(1).map(line => {
        const c = line.split(',');
        return { apr: parseFloat(c[aprIdx]), twi: parseFloat(c[twiIdx]), agb: parseFloat(c[agbIdx]) };
      }).filter(r => isFinite(r.apr) && isFinite(r.twi) && isFinite(r.agb));

      // Subsample evenly to MAX_SCATTER_PTS
      const step = allRows.length > MAX_SCATTER_PTS ? Math.ceil(allRows.length / MAX_SCATTER_PTS) : 1;
      const rows  = allRows.filter((_, i) => i % step === 0);

      loadingEl.style.display = 'none';

      // Chart 1 — S2 Apr 1 water vs AGB (with regression)
      waterWrap.appendChild(makeScatter({
        xs: rows.map(r => r.apr),
        ys: rows.map(r => r.agb),
        xLabel: 'S2 reflectance · dry-season onset (Apr 1)',
        yLabel: 'AGB (MgC/ha)',
        color: '#7ecfef',
        regression: true,
      }));

      // Chart 2 — TWI vs AGB
      twiWrap.appendChild(makeScatter({
        xs: rows.map(r => r.twi),
        ys: rows.map(r => r.agb),
        xLabel: 'TWI (0–255)',
        yLabel: 'AGB (MgC/ha)',
        xMin: 0, xMax: 255,
        color: '#41b6c4',
        regression: true,
      }));

    } catch (e) {
      loadingEl.style.display = 'none';
      console.error('[SceneHydroManagement] chart fetch failed', e);
    }
  }

  private rawMap(): maplibregl.Map {
    return (this.mapAdapter as unknown as { map: maplibregl.Map }).map;
  }
}
